import React from 'react';
import {Link, view} from '@sail/ui';
import {message, useIntl} from '@sail/intl';
import {useAnalytics} from '@sail/observability';
import {PoweredByStripe} from './PoweredByStripe';
import {useMerchantData} from '../../context';

const messages = {
  contactSupport: message({
    id: 'connectEmbedLib.overlay.contactSupportButton',
    defaultMessage: 'Contact',
    description: 'Contact support button label in the drawer component',
  }),

  privacyPolicy: message({
    id: 'connectEmbedLib.overlay.privacyPolicy',
    defaultMessage: 'Privacy',
    description: 'Link to Stripe privacy policy',
  }),

  terms: message({
    id: 'connectEmbedLib.overlay.terms',
    defaultMessage: 'Terms',
    description: 'Link to Stripe terms and conditions',
  }),
};

type ContactSupportProps = {
  showContactSupportForm?: boolean;
  showSupportOverlay?: boolean;
  handleShowSupport?: () => void;
  supportSiteLink?: string | null;
};

export const ContactSupportButton: React.FC<ContactSupportProps> = ({
  handleShowSupport,
  supportSiteLink,
  showSupportOverlay,
  showContactSupportForm,
}): JSX.Element => {
  const intl = useIntl();
  return (
    <>
      {supportSiteLink ? (
        <Link
          type="secondary"
          href={supportSiteLink}
          target="_blank"
          rel="noopener"
          css={{fontWeight: 'normal'}}
        >
          {intl.formatMessage(messages.contactSupport)}
        </Link>
      ) : (
        !showSupportOverlay &&
        showContactSupportForm && (
          <Link css={{fontWeight: 'normal'}} onPress={handleShowSupport}>
            {intl.formatMessage(messages.contactSupport)}
          </Link>
        )
      )}
    </>
  );
};

type PrivacyPolicyProps = {
  hasInjectedPrivacyPolicy: boolean;
  privacyPolicyUrl: string;
};

const PrivacyPolicyButton: React.FC<PrivacyPolicyProps> = ({
  hasInjectedPrivacyPolicy,
  privacyPolicyUrl,
}): JSX.Element => {
  const intl = useIntl();
  const analytics = useAnalytics();

  return React.useMemo(() => {
    if (hasInjectedPrivacyPolicy) {
      analytics.viewed('injected_privacy_policy_url', {url: privacyPolicyUrl});
    }

    return (
      <>
        <Link
          type="secondary"
          href={privacyPolicyUrl}
          target="_blank"
          rel="noopener"
          css={{fontWeight: 'normal'}}
        >
          {intl.formatMessage(messages.privacyPolicy)}
        </Link>
      </>
    );
  }, [analytics, intl, privacyPolicyUrl, hasInjectedPrivacyPolicy]);
};

type TermsProps = {
  serviceAgreement?: string;
  hasInjectedTos: boolean;
  termsOfServiceUrl: string;
};

const TermsButton: React.FC<TermsProps> = ({
  serviceAgreement,
  hasInjectedTos,
  termsOfServiceUrl,
}): JSX.Element => {
  const intl = useIntl();
  const analytics = useAnalytics();

  return React.useMemo(() => {
    if (hasInjectedTos) {
      analytics.viewed('injected_terms_url', {
        url: termsOfServiceUrl,
        serviceAgreement,
      });
    }

    return (
      <>
        <Link
          type="secondary"
          href={termsOfServiceUrl}
          target="_blank"
          rel="noopener"
          css={{fontWeight: 'normal'}}
        >
          {intl.formatMessage(messages.terms)}
        </Link>
      </>
    );
  }, [analytics, intl, serviceAgreement, termsOfServiceUrl, hasInjectedTos]);
};

type OnboardingButtonsProps = TermsProps & PrivacyPolicyProps;
const OnboardingButtons: React.FC<OnboardingButtonsProps> = ({
  serviceAgreement,
  hasInjectedPrivacyPolicy,
  privacyPolicyUrl,
  hasInjectedTos,
  termsOfServiceUrl,
}) => {
  return (
    <>
      <PrivacyPolicyButton
        hasInjectedPrivacyPolicy={hasInjectedPrivacyPolicy}
        privacyPolicyUrl={privacyPolicyUrl}
      />
      <TermsButton
        serviceAgreement={serviceAgreement}
        hasInjectedTos={hasInjectedTos}
        termsOfServiceUrl={termsOfServiceUrl}
      />
    </>
  );
};

export type LegalLinksProps = {shouldRenderLegalLinks?: boolean} & TermsProps &
  PrivacyPolicyProps;

type Props = ContactSupportProps & {
  legalLinks?: LegalLinksProps;
  isResponsiveDesignEnabled?: boolean;
};

// Show support overlay here checks to see if the support form is already being shown
export const OverlayStripeFooter = ({
  legalLinks,
  isResponsiveDesignEnabled = false,
  ...props
}: Props): JSX.Element | null => {
  const {showContactSupportForm, supportSiteLink} = props;
  const {showSupportLinks} = useMerchantData() || {};
  const showStripeBranding = Boolean(
    showSupportLinks && (supportSiteLink || showContactSupportForm),
  );
  const shouldRenderLegalLinks = legalLinks?.shouldRenderLegalLinks;

  if (!showStripeBranding && !shouldRenderLegalLinks) {
    return null;
  }

  if (showStripeBranding) {
    return (
      <view.div
        css={{
          stack: 'y',
          gap: 'medium',
        }}
      >
        {showStripeBranding && (
          <view.div
            css={{
              stack: 'x',
              alignY: isResponsiveDesignEnabled ? 'top' : 'center',
              distribute: 'space-between',
              marginTop: 'auto',
            }}
          >
            <view.div
              css={{
                alignX: 'start',
              }}
            >
              <PoweredByStripe />
            </view.div>
            <view.div
              css={{
                alignX: 'end',
                font: 'label.medium',
              }}
            >
              <view.div
                css={{
                  stack: 'x',
                  alignX: isResponsiveDesignEnabled ? 'end' : 'center',
                  alignY: 'center',
                  distribute: 'packed',
                  gap: 'medium',
                  font: 'body.small',
                  fontWeight: 'label.medium',
                  ...(isResponsiveDesignEnabled ? {wrap: 'wrap'} : null),
                }}
              >
                {shouldRenderLegalLinks && (
                  <OnboardingButtons {...legalLinks} />
                )}
                <ContactSupportButton {...props} />
              </view.div>
            </view.div>
          </view.div>
        )}
      </view.div>
    );
  }

  return (
    <view.div
      css={{
        stack: 'x',
        alignX: isResponsiveDesignEnabled ? 'start' : 'center',
        distribute: 'packed',
        gap: 'medium',
        ...(isResponsiveDesignEnabled ? {wrap: 'wrap'} : null),
      }}
    >
      {shouldRenderLegalLinks && <OnboardingButtons {...legalLinks} />}
    </view.div>
  );
};
